 
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Layout, ListItem, Select, TextArea, TextField} from '@phonectas/unifon-ui-kit';
import {isValidPhone} from 'utils/phoneNumber';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {Invoice} from 'common/types/store/threeSixtyTypes';

interface SmsContent {
	number: string;
	text: string;
}

interface InvoiceSendSmsContentProps {
	data: Invoice;
	setSmsContent: React.Dispatch<React.SetStateAction<SmsContent>>;
	smsContent: SmsContent;
}

interface TemplateOption {
    value: string;
    label: string;
    message: string;
}

const InvoiceSendSmsContent: React.FC<InvoiceSendSmsContentProps> = ({data, setSmsContent, smsContent}) => {
	const [t] = useTranslation();
	const {getConfigTemplates} = useThreeSixtyNewStore((state) => ({getConfigTemplates: state.getConfigTemplates}), shallow);
	const {smsData} = useThreeSixtyNewStore((state) => ({
		smsData: state.smsDetails,
	}), shallow);
	const [templates, setTemplates] = useState<TemplateOption[]>([]);

	const handleChangeNumber = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const {value} = e.target;
		setSmsContent((prev) => ({
			...prev,
			number: value,
		}));
	}, [setSmsContent]);
	const handleSelectTemplate = useCallback(
		(val: unknown, _e: React.SyntheticEvent, _name: string) => {
			setSmsContent((prev) => ({
				...prev,
				text: (val as string) ?? '',
			}));
		},
		[setSmsContent]
	);

	const handleSmsText = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setSmsContent((prev) => ({
				...prev,
				text: e.target.value,
			}));
		},
		[setSmsContent]
	);

	const setSmsTextDirect = useCallback(
		(text: string) => {
			setSmsContent((prev) => ({
				...prev,
				text,
			}));
		},
		[setSmsContent]
	);

	const isValidPhoneNumber = useCallback((val: string | number) => {
		const strVal = typeof val === 'number' ? val.toString() : val;
		return strVal !== '' || isValidPhone(strVal);
	}, []);

	useEffect(() => {
		if (data?.invoice_number && data?.client_no) {
			getConfigTemplates('sms_invoice', data.invoice_number, data.client_no);
		}
	}, [data, getConfigTemplates]);

	useEffect(() => {
		if (
			Array.isArray(smsData) &&
			smsData.length > 0 &&
			smsData[0]?.lang &&
			Array.isArray(smsData[0].lang) &&
			smsData[0].lang.length > 0
		) {
			const langData = smsData[0].lang[0];
			const updatedTemplates: TemplateOption[] = [
				{
					value: '1',
					label: t('Norwegian'),
					message: langData?.no ?? '',
				},
				{
					value: '2',
					label: t('English'),
					message: langData?.en ?? '',
				},
			];
			setTemplates(updatedTemplates);
			setSmsTextDirect(updatedTemplates[0]?.message || '');
		}
	}, [smsData, t, handleSmsText]);

	return (
		<Layout direction="column" fullWidth>
			<Layout direction="row" nowrap style={{width: '500px'}}>
				<TextField
					label={t('Phone number')}
					fullWidth
					value={smsContent.number}
					onChange={handleChangeNumber}
					validator={isValidPhoneNumber}
					inputProps={{maxLength: 11}}
					errorMessagePlacement="none"
				/>
				<Select
					label={t('SMS templates')}
					fullWidth
					value={smsContent.text}
					onChange={handleSelectTemplate}
				>
					{
						templates.map((template) => (
							<ListItem
								key={template.value}
								value={template.message}
								primaryText={template.label}
								selected={smsContent.text === template.message}
							/>
						))
					}
				</Select>
			</Layout>
			<Layout direction="row" nowrap style={{width: '500px'}}>
				<TextArea
					placeholder={t('Message')}
					fullWidth
					value={smsContent.text}
					onChange={handleSmsText}
					style={{height: '200px', whiteSpace: 'pre-wrap'}}
				/>
			</Layout>
		</Layout>
	);
}

export default React.memo(InvoiceSendSmsContent);
